import React from 'react';
import axios from 'axios';
import { FaPlus, FaRandom } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import uuid from 'react-uuid';
import { deepCloneArray } from '../../utils/array';
import { getId } from '../../utils/item';
import {
  createNewItem,
  dupplicateItem,
  update,
} from '../../services/item';
import { setDrawerOpen } from '../../store/global';
import { Checkbox } from '../form/checkbox';
import { BtnSimple } from '../form/btn.Simple';
import { SettingsSelectCustom } from '../form/settings.Widget';
import { TextMd } from '../text/text.Md';
import {
  applyGeneratorGroupRules,
  normalizeGeneratorConfig,
  runGenerator,
} from '../../utils/generator';
import { GeneratorSettings } from './generator.Settings';
import { useGeneratorPersistence } from './hooks/useGeneratorPersistence';

export const GeneratorGlobal = props => {
  const { itemId, generatorId, generator, canEdit } = props;
  const dispatch = useDispatch();
  const item = useSelector(state => state.items?.[itemId]);
  const items = useSelector(state => state.items);
  const canToggleControls = Boolean(item?._id);
  const normalized = React.useMemo(
    () => normalizeGeneratorConfig(generator, generatorId),
    [generator, generatorId],
  );
  const [lastResultLocal, setLastResultLocal] = React.useState(null);

  const controlsState = React.useMemo(
    () =>
      normalized.runtime && typeof normalized.runtime.controlsState === 'object'
        ? normalized.runtime.controlsState
        : {},
    [normalized.runtime],
  );
  const lastResultPersisted =
    normalized.lastResult && typeof normalized.lastResult === 'object'
      ? normalized.lastResult
      : null;
  const lastResult = lastResultLocal || lastResultPersisted;
  const resultValues = Array.isArray(lastResult?.values)
    ? lastResult.values
    : [];
  const labelValues = resultValues
    .filter(entry => entry?.target?.kind === 'item_label')
    .map(entry => entry?.value)
    .filter(value => typeof value === 'string' && value.trim() !== '');
  const otherValues = resultValues.filter(
    entry => entry?.target?.kind !== 'item_label',
  );
  const itemCreationEnabled = normalized?.itemCreation?.enabled !== false;
  const openCreatedItemInSidebar =
    normalized?.itemCreation?.openInSidebar !== false;
  const outputMapping =
    normalized?.itemCreation?.outputMapping &&
    typeof normalized.itemCreation.outputMapping === 'object'
      ? normalized.itemCreation.outputMapping
      : {};
  const generatedLinkedItemIds = React.useMemo(
    () =>
      Array.from(
        new Set(
          (normalized.controls || [])
            .filter(
              control =>
                controlsState?.[control.id] === true &&
                typeof control?.linkedItemId === 'string' &&
                control.linkedItemId.trim().length > 0,
            )
            .map(control => control.linkedItemId)
            .filter(Boolean),
        ),
      ),
    [controlsState, normalized.controls],
  );

  const ensureBidirectionalGeneratedLinks = React.useCallback(
    async createdItem => {
      const createdItemId = createdItem?._id;
      if (!createdItemId || generatedLinkedItemIds.length === 0) {
        return;
      }

      const buildLinkEntry = linkId => ({
        [linkId]: { id: linkId, x: 0, y: 0, w: 4, h: 1, i: linkId },
      });

      const addMissingLink = (links, linkId) => {
        const current = Array.isArray(links) ? deepCloneArray(links) : [];
        const exists = current.some(linkObj => getId(linkObj) === linkId);
        if (exists) {
          return { changed: false, links: current };
        }
        current.push(buildLinkEntry(linkId));
        return { changed: true, links: current };
      };

      let createdLinks = Array.isArray(createdItem?.links)
        ? deepCloneArray(createdItem.links)
        : null;

      if (!Array.isArray(createdLinks)) {
        try {
          const resCreated = await axios.get(
            `${process.env.REACT_APP_API_URL}/items/${createdItemId}`,
          );
          createdLinks = Array.isArray(resCreated?.data?.links)
            ? deepCloneArray(resCreated.data.links)
            : [];
        } catch (error) {
          createdLinks = [];
        }
      }

      let createdChanged = false;
      let nextCreatedLinks = createdLinks;
      generatedLinkedItemIds.forEach(linkedId => {
        if (!linkedId || linkedId === createdItemId) {
          return;
        }
        const result = addMissingLink(nextCreatedLinks, linkedId);
        if (result.changed) {
          createdChanged = true;
          nextCreatedLinks = result.links;
        }
      });
      if (createdChanged) {
        await update(`items/${createdItemId}`, 'links', nextCreatedLinks);
      }

      await Promise.all(
        generatedLinkedItemIds.map(async linkedId => {
          if (!linkedId || linkedId === createdItemId) {
            return;
          }
          const cachedLinked = items?.[linkedId];
          let linkedLinks = Array.isArray(cachedLinked?.links)
            ? deepCloneArray(cachedLinked.links)
            : null;
          if (!Array.isArray(linkedLinks)) {
            try {
              const resLinked = await axios.get(
                `${process.env.REACT_APP_API_URL}/items/${linkedId}`,
              );
              linkedLinks = Array.isArray(resLinked?.data?.links)
                ? deepCloneArray(resLinked.data.links)
                : [];
            } catch (error) {
              return;
            }
          }
          const result = addMissingLink(linkedLinks, createdItemId);
          if (result.changed) {
            await update(`items/${linkedId}`, 'links', result.links);
          }
        }),
      );
    },
    [generatedLinkedItemIds, items],
  );

  const { withCurrentGenerator } = useGeneratorPersistence({
    item,
    itemId,
    generatorId,
  });

  const setControlValue = (controlId, nextValue, options = {}) => {
    if (!canToggleControls || !controlId || typeof nextValue !== 'boolean') {
      return;
    }
    const { forceExclusive = false } = options;
    withCurrentGenerator(current => {
      const currentControlsState =
        current.runtime && typeof current.runtime.controlsState === 'object'
          ? current.runtime.controlsState
          : {};
      const group = (current.groups || []).find(entry =>
        (entry.controlIds || []).includes(controlId),
      );

      let draftNextState = {
        ...currentControlsState,
        [controlId]: nextValue,
      };

      // For exclusive groups, an explicit click on one option should switch to it.
      if (
        group &&
        Array.isArray(group.controlIds) &&
        nextValue &&
        (forceExclusive || group.controlIds.length > 1)
      ) {
        const groupControlIds = group.controlIds.filter(Boolean);
        groupControlIds.forEach(id => {
          draftNextState[id] = id === controlId;
        });
      }

      const nextControlsState = applyGeneratorGroupRules(
        draftNextState,
        current.groups || [],
      );

      return {
        ...current,
        runtime: {
          ...(current.runtime || {}),
          controlsState: nextControlsState,
        },
      };
    });
  };

  const toggleControl = controlId => {
    const currentValue = controlsState?.[controlId] === true;
    setControlValue(controlId, !currentValue);
  };

  const selectControl = controlId => {
    setControlValue(controlId, true, { forceExclusive: true });
  };

  const controlById = React.useMemo(
    () =>
      Object.fromEntries((normalized.controls || []).map(control => [control.id, control])),
    [normalized.controls],
  );

  const controlBlocks = React.useMemo(() => {
    const groups = Array.isArray(normalized.groups) ? normalized.groups : [];
    const groupedControlIds = new Set();
    groups.forEach(group => {
      (group?.controlIds || []).forEach(controlId => groupedControlIds.add(controlId));
    });
    const consumedGroupIds = new Set();
    const blocks = [];
    const ungroupedControls = [];

    (normalized.controls || []).forEach(control => {
      if (!control?.id) {
        return;
      }
      const group = groups.find(entry => (entry?.controlIds || []).includes(control.id));
      if (group?.id) {
        if (consumedGroupIds.has(group.id)) {
          return;
        }
        consumedGroupIds.add(group.id);
        const controls = (group.controlIds || [])
          .map(controlId => controlById[controlId])
          .filter(Boolean);
        if (controls.length === 0) {
          return;
        }
        blocks.push({
          id: `group:${group.id}`,
          isGroup: true,
          label: group.label || controls[0]?.label || 'Groupe',
          displayMode: group.displayMode || 'buttons',
          controls,
        });
        return;
      }

      if (!groupedControlIds.has(control.id)) {
        ungroupedControls.push(control);
      }
    });

    if (ungroupedControls.length > 0) {
      blocks.push({
        id: 'single:all',
        isGroup: false,
        label: 'Options',
        displayMode: 'buttons',
        controls: ungroupedControls,
      });
    }

    return blocks;
  }, [controlById, normalized.controls, normalized.groups]);
  const shouldShowControls = (normalized.controls || []).length > 1;

  const handleGenerate = event => {
    event?.stopPropagation?.();
    if (!item?._id) {
      return;
    }
    withCurrentGenerator(current => {
      const generatorsById = {};
      (item?.generators || []).forEach(generatorEntry => {
        const id = getId(generatorEntry);
        if (!id) {
          return;
        }
        generatorsById[id] = generatorEntry?.[id] || null;
      });
      const result = runGenerator(current, { generatorsById });
      setLastResultLocal(result);
      return {
        ...current,
        lastResult: null,
      };
    });
  };

  const handleCreateItem = async event => {
    event?.stopPropagation?.();
    if (!canEdit || !item?._id || !lastResult) {
      return;
    }

    const values = Array.isArray(lastResult.values) ? lastResult.values : [];
    const mappedLabelParts = values
      .filter(entry => {
        const mapping = outputMapping?.[entry?.outputId || ''] || {};
        if (mapping?.kind === 'label') {
          return true;
        }
        if (!mapping?.kind) {
          return entry?.target?.kind === 'item_label';
        }
        return false;
      })
      .map(entry => (typeof entry?.value === 'string' ? entry.value.trim() : ''))
      .filter(Boolean)
      .join(' ');
    const itemLabel = mappedLabelParts || lastResult.summaryLabel || 'Nouveau';

    const textValues = values
      .filter(
        entry =>
          entry?.target?.kind !== 'item_label' && entry?.target?.kind !== 'num',
      )
      .map(entry => entry?.value)
      .filter(value => typeof value === 'string' && value.trim() !== '');
    const numValues = values
      .filter(entry => entry?.target?.kind === 'num')
      .map(entry => ({
        label: entry?.label || 'Valeur',
        value: Number(entry?.value),
      }))
      .filter(entry => Number.isFinite(entry.value));

    const textPayload =
      textValues.length > 0
        ? textValues.map(textValue => {
            const textId = uuid().replaceAll('-', '');
            return {
              [textId]: {
                text: textValue,
                editing: false,
                x: 0,
                y: 0,
                w: 4,
                h: 1,
                i: textId,
              },
            };
          })
        : undefined;
    const numPayload =
      numValues.length > 0
        ? numValues.map(numEntry => {
            const numId = uuid().replaceAll('-', '');
            return {
              [numId]: {
                i: numId,
                label: numEntry.label,
                value: numEntry.value,
                mod: 0,
                rounded: 'down',
                formula: null,
                xp: { min: 0, max: 0, value: 0 },
                x: 0,
                y: 0,
                w: 4,
                h: 1,
              },
            };
          })
        : undefined;

    const applyOutputMappingToCreatedItem = async createdItem => {
      if (!createdItem?._id) {
        return;
      }
      const nextTexts = deepCloneArray(createdItem?.text || []);
      const nextNums = deepCloneArray(createdItem?.has_num || []);

      const upsertText = (fieldId, textValue) => {
        const nextValue = typeof textValue === 'string' ? textValue : String(textValue ?? '');
        if (fieldId) {
          const textIdx = nextTexts.findIndex(obj => getId(obj) === fieldId);
          if (textIdx > -1) {
            nextTexts[textIdx] = {
              [fieldId]: {
                ...(nextTexts[textIdx]?.[fieldId] || {}),
                text: nextValue,
              },
            };
            return;
          }
        }
        const newTextId = uuid().replaceAll('-', '');
        nextTexts.push({
          [newTextId]: {
            text: nextValue,
            editing: false,
            x: 0,
            y: 0,
            w: 4,
            h: 1,
            i: newTextId,
          },
        });
      };

      const upsertNum = (fieldId, outputLabel, numValue) => {
        const safeValue = Number(numValue);
        if (!Number.isFinite(safeValue)) {
          return;
        }
        if (fieldId) {
          const numIdx = nextNums.findIndex(obj => getId(obj) === fieldId);
          if (numIdx > -1) {
            nextNums[numIdx] = {
              [fieldId]: {
                ...(nextNums[numIdx]?.[fieldId] || {}),
                value: safeValue,
              },
            };
            return;
          }
        }
        const newNumId = uuid().replaceAll('-', '');
        nextNums.push({
          [newNumId]: {
            i: newNumId,
            label: outputLabel || 'Valeur',
            value: safeValue,
            mod: 0,
            rounded: 'down',
            formula: null,
            xp: { min: 0, max: 0, value: 0 },
            x: 0,
            y: 0,
            w: 4,
            h: 1,
          },
        });
      };

      values.forEach(entry => {
        const entryTargetKind = entry?.target?.kind || 'text';
        const mapping = outputMapping?.[entry?.outputId || ''] || {};
        const mappedKind =
          mapping?.kind ||
          (entryTargetKind === 'item_label'
            ? 'label'
            : entryTargetKind === 'num'
              ? 'num'
              : 'text');

        if (mappedKind === 'label') {
          return;
        }

        if (mappedKind === 'text') {
          upsertText(mapping?.fieldId || null, entry?.value || '');
          return;
        }

        if (mappedKind === 'num') {
          upsertNum(mapping?.fieldId || null, entry?.label, entry?.value);
        }
      });

      await update(`items/${createdItem._id}`, 'label', itemLabel);
      await update(`items/${createdItem._id}`, 'text', nextTexts);
      await update(`items/${createdItem._id}`, 'has_num', nextNums);
      await ensureBidirectionalGeneratedLinks(createdItem);
    };

    const templateItemId = normalized?.itemCreation?.templateItemId || null;
    if (templateItemId) {
      const cachedTemplate = items?.[templateItemId];
      let templateItem = cachedTemplate;
      const hasCompleteTemplate =
        templateItem &&
        !templateItem.__partial &&
        Array.isArray(templateItem?.text) &&
        Array.isArray(templateItem?.has_num) &&
        Array.isArray(templateItem?.tabs);

      if (!hasCompleteTemplate) {
        try {
          const resTemplate = await axios.get(
            `${process.env.REACT_APP_API_URL}/items/${templateItemId}`,
          );
          if (resTemplate?.data?._id) {
            templateItem = resTemplate.data;
          }
        } catch (error) {
          templateItem = null;
        }
      }

      if (templateItem?._id) {
        await dupplicateItem(
          {
            ...templateItem,
            label: itemLabel,
            callback: async createdItem => {
              await applyOutputMappingToCreatedItem(createdItem);
              if (openCreatedItemInSidebar && createdItem?._id) {
                dispatch(setDrawerOpen(createdItem._id));
              }
            },
          },
          item,
        );
        return;
      }
    }

    const res = await createNewItem(
      {
        label: itemLabel,
        text: textPayload,
        has_num: numPayload,
      },
      item,
    );
    const createdItemId = res?.data?.item?._id;
    if (createdItemId) {
      await ensureBidirectionalGeneratedLinks(
        res?.data?.item || { _id: createdItemId, links: [] },
      );
    }
    if (openCreatedItemInSidebar && createdItemId) {
      dispatch(setDrawerOpen(createdItemId));
    }
  };

  return (
    <div className='h-full w-full overflow-hidden'>
      {canEdit && (
        <GeneratorSettings
          itemId={itemId}
          generatorId={generatorId}
          generator={generator}
          canEdit={canEdit}
        />
      )}

      <div className='w-full h-full p-1 flex flex-col gap-05 min-h-0 overflow-y-auto overflow-x-hidden'>
        {shouldShowControls && (
          <div className='flex flex-col gap-03 board-grid-no-drag'>
            {controlBlocks.length === 0 && (
              <div className='text-xs text-contrast text-opacity-60'>
                aucun contrôle
              </div>
            )}
            {controlBlocks.map(block => {
            const isExclusiveGroup = block.isGroup && block.controls.length > 1;
            const displayMode =
              block.displayMode === 'checkbox' || block.displayMode === 'dropdown'
                ? block.displayMode
                : 'buttons';

            if (displayMode === 'dropdown' && isExclusiveGroup) {
              const selectedControl =
                block.controls.find(control => controlsState[control.id] === true) ||
                block.controls[0];
              return (
                <div
                  key={block.id}
                  className='flex items-center gap-05'
                >
                  <div className='text-xs uppercase text-contrast text-opacity-70 min-w-90'>
                    {block.label}
                  </div>
                  <div
                    className='w-170'
                    onClick={event => event.stopPropagation()}
                  >
                    <SettingsSelectCustom
                      size='sm'
                      value={selectedControl?.id || ''}
                      disabled={!canToggleControls}
                      options={block.controls.map(control => ({
                        value: control.id,
                        label: control.label || 'Option',
                      }))}
                      onChange={event => {
                        selectControl(event?.target?.value);
                      }}
                    />
                  </div>
                </div>
              );
            }

            if (displayMode === 'checkbox') {
              return (
                <div
                  key={block.id}
                  className='flex flex-wrap items-center gap-05'
                >
                  {block.controls.map(control => {
                    const active = controlsState[control.id] === true;
                    return (
                      <div
                        key={`${block.id}:${control.id}`}
                        onClick={event => event.stopPropagation()}
                      >
                        <Checkbox
                          type={isExclusiveGroup ? 'radio' : 'checkbox'}
                          checked={active}
                          label={control.label || 'Option'}
                          className='text-xs'
                          labelClassName='text-xs'
                          disabled={!canToggleControls}
                          onChange={checked => {
                            if (isExclusiveGroup) {
                              if (checked) {
                                selectControl(control.id);
                              }
                              return;
                            }
                            setControlValue(control.id, checked);
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              );
            }

            return (
              <div
                key={block.id}
                className='flex flex-wrap gap-03'
              >
                {block.controls.map(control => {
                  const active = controlsState[control.id] === true;
                  return (
                    <button
                      key={`${block.id}:${control.id}`}
                      type='button'
                      className={`rounded px-05 py-01 text-xs transition ${
                        active
                          ? 'bg-primary text-contrast'
                          : 'bg-main bg-opacity-40 text-contrast text-opacity-70'
                      } ${
                        canToggleControls
                          ? 'hover:opacity-60 cursor-pointer'
                          : 'cursor-default opacity-80'
                      }`}
                      disabled={!canToggleControls}
                      onClick={event => {
                        event.stopPropagation();
                        toggleControl(control.id);
                      }}
                    >
                      {control.label || 'Option'}
                    </button>
                  );
                })}
              </div>
            );
            })}
          </div>
        )}
        <div className='board-grid-no-drag flex justify-end'>
          <BtnSimple
            className='text-xs bg-primary'
            onClick={handleGenerate}
          >
            <span className='inline-flex items-center gap-03'>
              <FaRandom />
              <span>générer</span>
            </span>
          </BtnSimple>
        </div>
        {lastResult && (
          <div className='mt-2 flex flex-col gap-05 text-xs text-contrast board-grid-no-drag'>
            {labelValues.length > 0 && (
              <div className='text-center font-condensed uppercase rounded bg-primary bg-opacity-50 px-05 py-03 text-sm font-semibold whitespace-pre-wrap'>
                {labelValues.join(' ')}
              </div>
            )}
            {otherValues.map(entry => (
              <div
                key={entry.outputId || entry.label}
                className='px-03'
              >
                {entry?.target?.kind === 'num' ? (
                  <>
                    <span className='text-xs uppercase'>
                      {entry?.label || 'Num'}:{' '}
                    </span>
                    {entry.value === 0
                      ? '0'
                      : entry.value === null ||
                          entry.value === undefined ||
                          entry.value === ''
                        ? '-'
                        : String(entry.value)}
                  </>
                ) : (
                  <div style={{ pointerEvents: 'none' }}>
                    <TextMd
                      str={
                        entry.value === null ||
                        entry.value === undefined ||
                        entry.value === ''
                          ? '-'
                          : String(entry.value)
                      }
                      textId={`generator-preview-${generatorId}-${
                        entry.outputId || entry.label || 'output'
                      }`}
                      itemId={itemId}
                      style={{ width: '100%', color: 'inherit' }}
                    />
                  </div>
                )}
              </div>
            ))}
            {resultValues.length === 0 && (
              <div className='text-contrast text-opacity-60'>
                aucun résultat
              </div>
            )}
            {itemCreationEnabled && (
              <div className='flex justify-end'>
                <BtnSimple
                  className='text-xs bg-primary'
                  disabled={!canEdit}
                  onClick={handleCreateItem}
                >
                  <span className='inline-flex items-center gap-03'>
                    <FaPlus />
                    <span>créer un item</span>
                  </span>
                </BtnSimple>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
